'use client';

import { Building2, Check, ChevronDown, Layers3 } from 'lucide-react';
import { usePathname, useSearchParams } from 'next/navigation';
import { Badge, Tooltip } from '@statura/ui';
import { Banner } from '@statura/ui/src/components/detail-page/banner';
import { WORKSPACE_LABELS, type ProviderProgramContext } from '@/lib/navigation/provider-type';
import type { ProviderType } from '@/lib/navigation/nav-v2';
import { isConfirmedEmptyProviderContext } from '@/lib/navigation/program-route-policy';
import { ProgramLensActionButton } from './program-lens-action-button';

const PROVIDER_LABEL: Record<ProviderType, { short: string; full: string }> = {
  RAC: { short: 'Residential aged care', full: 'Residential aged care only' },
  SAH: { short: 'Support at Home', full: 'Support at Home only' },
  MIXED: { short: 'Choose programme', full: 'Choose a current programme workspace' },
  OTHER: { short: 'Other', full: 'Other registered care programs' },
  UNKNOWN: { short: 'Pending', full: 'Provider program not yet resolved' },
};

const MIXED_WORKSPACE_OPTIONS: ReadonlyArray<{
  workspace: 'HOME_COMMUNITY' | 'RESIDENTIAL' | 'MIXED';
  label: string;
}> = [
  { workspace: 'HOME_COMMUNITY', label: 'Home & community' },
  { workspace: 'RESIDENTIAL', label: 'Residential' },
  { workspace: 'MIXED', label: 'Mixed' },
];

/**
 * Persistent, server-validated programme lens. A pure single-family tenant is
 * read-only. Multi-family tenants choose one exact current family; the action
 * re-reads program_enrollments before persisting the tenant/revision-bound
 * HttpOnly preference.
 */
export function ProviderChip({
  providerType,
  programContext,
  tenantId,
  compact = false,
}: {
  providerType: ProviderType;
  programContext?: ProviderProgramContext;
  tenantId?: string | null;
  compact?: boolean;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const confirmedEmpty =
    providerType === 'UNKNOWN' &&
    programContext &&
    isConfirmedEmptyProviderContext(programContext, tenantId);
  const meta = confirmedEmpty
    ? { short: 'Shared workspace', full: 'Shared workspace' }
    : PROVIDER_LABEL[providerType];
  const activeOption = programContext?.lensOptions.find(
    (option) => option.family === programContext.activeProgramFamily,
  );
  const isHomeCommunityWorkspace = programContext?.workspace === 'HOME_COMMUNITY';
  const workspaceLabel = programContext?.workspace
    ? WORKSPACE_LABELS[programContext.workspace]
    : null;
  const activeFamilyLabel = activeOption?.label ?? meta.full;
  const fullLabel = workspaceLabel ?? activeFamilyLabel;
  const shortLabel =
    (programContext?.workspace === 'HOME_COMMUNITY' ? 'Home care' : workspaceLabel) ??
    (activeOption ? activeOption.label : meta.short);
  const selectable = Boolean(
    programContext &&
    (programContext.workspace === 'MIXED' ||
      (!programContext.workspace &&
        (programContext.lensOptions.length > 1 ||
          (programContext.selectionInvalid && programContext.lensOptions.length > 0)))),
  );
  const iconOnly = compact || (shortLabel.length > 21 && !programContext?.selectionRequired);
  const returnTo = `${pathname}${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`;
  const chip = (
    <Badge
      variant="neutral"
      dot={false}
      className={`min-w-0 self-center gap-1 focus:outline focus:outline-2 focus:outline-focus focus:-outline-offset-2 border-control-border/30 bg-selected text-selected-foreground ${compact ? 'max-w-11 justify-center px-1.5' : 'w-auto shrink-0'}`}
      aria-label={selectable ? undefined : `Current programme workspace: ${fullLabel}`}
      tabIndex={!selectable && iconOnly ? 0 : undefined}
      data-program-lens={programContext?.activeProgramFamily ?? 'SHARED'}
    >
      {programContext?.selectionRequired ? (
        <Layers3 className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
      ) : (
        <Building2 className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
      )}
      <span className={iconOnly ? 'sr-only' : 'whitespace-nowrap'}>{shortLabel}</span>
      {selectable ? <ChevronDown className="h-2.5 w-2.5 shrink-0" aria-hidden="true" /> : null}
    </Badge>
  );

  if (!selectable || !programContext) {
    return (
      <Tooltip
        content={
          isHomeCommunityWorkspace
            ? 'Home & community shows all your community clients together. Support at Home screens list only Support at Home funded clients.'
            : `${fullLabel}. Programme-specific items outside this workspace are hidden.`
        }
        side={compact ? 'right' : 'bottom'}
      >
        {chip}
      </Tooltip>
    );
  }

  return (
    <details className="group relative" data-program-lens-selector="true">
      <summary
        className="list-none cursor-pointer rounded-badge focus:outline focus:outline-2 focus:outline-focus focus:-outline-offset-2"
        aria-label={`Current programme workspace: ${fullLabel}. Choose another programme.`}
      >
        {chip}
      </summary>
      <div
        className={`absolute z-50 mt-2 w-72 rounded-overlay border border-control-border/30 bg-surface-sheet p-2 text-left shadow-popover ${compact ? 'left-full top-0 ml-2 mt-0' : 'right-0'}`}
        role="group"
        aria-label="Programme workspace options"
      >
        <p className="px-2 py-1 text-caption font-semibold text-text-body">Programme workspace</p>
        <p className="px-2 pb-2 text-caption leading-5 text-text-muted">
          Navigation, search, help and dashboard data switch together.
        </p>
        {programContext.selectionInvalid ? (
          <Banner tone="warning" className="mx-1 mb-2">
            Your previous selection is no longer current. Choose again.
          </Banner>
        ) : null}
        <div className="space-y-1">
          {programContext.workspace === 'MIXED' ? (
            <>
              {MIXED_WORKSPACE_OPTIONS.map(({ workspace, label }) => (
                <ProgramLensActionButton
                  key={workspace}
                  returnTo={returnTo}
                  workspace={workspace}
                  className="flex w-full items-center gap-2 rounded-control px-2.5 py-2 text-left text-body text-text-body hover:bg-surface-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-wait disabled:opacity-70"
                  aria-current={programContext.workspace === workspace ? 'true' : undefined}
                >
                  <span className="min-w-0 flex-1">{label}</span>
                  {programContext.workspace === workspace ? (
                    <Check className="h-4 w-4 text-selected-foreground" />
                  ) : null}
                </ProgramLensActionButton>
              ))}
            </>
          ) : (
            programContext.lensOptions.map((option) => {
              const active = option.family === programContext.activeProgramFamily;
              return (
                <ProgramLensActionButton
                  key={option.family}
                  returnTo={returnTo}
                  programFamily={option.family}
                  className="flex w-full items-center gap-2 rounded-control px-2.5 py-2 text-left text-body text-text-body hover:bg-surface-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-wait disabled:opacity-70"
                  aria-current={active ? 'true' : undefined}
                >
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {active ? <Check className="h-4 w-4 text-selected-foreground" /> : null}
                </ProgramLensActionButton>
              );
            })
          )}
          {programContext.workspace !== 'MIXED' ? (
            <ProgramLensActionButton
              returnTo={returnTo}
              programFamily="SHARED"
              className="flex w-full items-center gap-2 rounded-control px-2.5 py-2 text-left text-body text-text-body hover:bg-surface-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-wait disabled:opacity-70"
              aria-current={programContext.selectionRequired ? 'true' : undefined}
            >
              <span className="min-w-0 flex-1">Shared workspace</span>
              {programContext.selectionRequired ? (
                <Check className="h-4 w-4 text-selected-foreground" />
              ) : null}
            </ProgramLensActionButton>
          ) : null}
        </div>
      </div>
    </details>
  );
}
